import { Box, Heading, HStack, Badge, Text, VStack } from "@chakra-ui/react-v3";
import {
  MedicationAdministration,
  MedicationDispense,
  MedicationStatement,
  MedicationRequest,
} from "@medplum/fhirtypes";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useFhirSdkContext } from "../side-panels/sidepanel-context";

type MedicationDisplayProps = {
  resource: unknown;
};

/**
 * Component that displays medication details and history for v2 sidepanel.
 * Based on the v1 MedicationContent implementation.
 */
export function MedicationDisplay({ resource }: MedicationDisplayProps) {
  const bundle = useFhirSdkContext();

  if (!resource || !bundle) {
    return null;
  }

  const resourceObj = resource as Record<string, unknown>;
  const resourceType = resourceObj.resourceType as string;

  // Check if this is a medication series (from table data)
  const isMedicationSeries =
    resourceObj.events && Array.isArray(resourceObj.events) && resourceObj.events.length > 0;

  if (isMedicationSeries) {
    return renderMedicationSeries(resourceObj);
  }

  // Check if this is a series data passed through the sidepanel (with resources array)
  const isSeriesData =
    resourceObj.resources &&
    Array.isArray(resourceObj.resources) &&
    resourceObj.resources.length > 0 &&
    resourceObj.codeDisplay;

  if (isSeriesData) {
    // Convert series data to the format expected by renderMedicationSeries
    const seriesData = {
      events: (resourceObj.resources as unknown[]).map((resource: unknown) => ({
        type:
          (resource as Record<string, unknown>).resourceType
            ?.toString()
            .toLowerCase()
            .replace("medication", "") || "unknown",
        date:
          getDateFromMedicationRef(
            resource as
              | MedicationAdministration
              | MedicationDispense
              | MedicationStatement
              | MedicationRequest
          ) || new Date().toISOString(),
        resource: resource,
      })),
      codeDisplay: resourceObj.codeDisplay,
    };

    return renderMedicationSeries(seriesData);
  }

  // Only show for individual medication-related resources
  if (
    ![
      "Medication",
      "MedicationAdministration",
      "MedicationDispense",
      "MedicationStatement",
      "MedicationRequest",
    ].includes(resourceType)
  ) {
    return null;
  }

  // Get all medication-related resources from the bundle
  const medicationAdministrations = bundle.getMedicationAdministrations();
  const medicationDispenses = bundle.getMedicationDispenses();
  const medicationStatements = bundle.getMedicationStatements();
  const medicationRequests = bundle.getMedicationRequests();

  // Filter resources that are related to the current medication
  const relatedResources = getRelatedMedicationResources(
    resource,
    medicationAdministrations,
    medicationDispenses,
    medicationStatements,
    medicationRequests
  );

  if (relatedResources.length === 0) {
    return null;
  }

  // Sort by date (newest first)
  const sortedResources = relatedResources.sort((a, b) => {
    const dateA = getDateFromMedicationRef(a);
    const dateB = getDateFromMedicationRef(b);
    if (!dateA || !dateB) return 0;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return (
    <Box>
      <Heading size="md" mb={4} color="fg.emphasized">
        Medication Details
      </Heading>

      {/* Medication Details Section */}
      <MedicationDetailsSection
        resources={sortedResources}
        medicationName={undefined} // Let it extract from resources
        bundle={bundle}
      />

      {/* Medication History Timeline */}
      <Heading size="md" mb={4} mt={6} color="fg.emphasized">
        Medication Timeline ({sortedResources.length})
      </Heading>
      <VStack align="stretch" gap={4}>
        {sortedResources.map((medResource, index) => (
          <MedicationTimelineItem
            key={`${medResource.resourceType}-${index}`}
            resource={medResource}
            isLast={index === sortedResources.length - 1}
          />
        ))}
      </VStack>
    </Box>
  );
}

function MedicationDetailsSection({
  resources,
  medicationName,
  bundle,
}: {
  resources: (
    | MedicationAdministration
    | MedicationDispense
    | MedicationStatement
    | MedicationRequest
  )[];
  medicationName?: string;
  bundle?: FhirBundleSdk;
}) {
  // Get the latest administration for details
  const latestAdmin = resources.find(r => r.resourceType === "MedicationAdministration") as
    | MedicationAdministration
    | undefined;
  const latestStatement = resources.find(r => r.resourceType === "MedicationStatement") as
    | MedicationStatement
    | undefined;

  // Try to get medication name from the actual Medication resource
  let medicationResourceName = null;
  if (bundle) {
    const medicationRef =
      latestStatement?.medicationReference?.reference ||
      latestAdmin?.medicationReference?.reference;
    if (medicationRef) {
      const medicationId = medicationRef.replace("Medication/", "");
      const medications = bundle.getMedications();
      const medication = medications.find(m => m.id === medicationId);
      if (medication) {
        medicationResourceName =
          medication.code?.text ||
          medication.code?.coding?.[0]?.display ||
          medication.code?.coding?.[0]?.code;
      }
    }
  }

  const finalMedicationName =
    medicationName ||
    medicationResourceName ||
    latestStatement?.medicationCodeableConcept?.text ||
    latestStatement?.medicationReference?.display ||
    latestAdmin?.medicationCodeableConcept?.text ||
    latestAdmin?.medicationReference?.display ||
    // Try to get medication name from the medication reference
    (latestStatement?.medicationReference?.reference
      ? latestStatement.medicationReference.reference.replace("Medication/", "")
      : null) ||
    (latestAdmin?.medicationReference?.reference
      ? latestAdmin.medicationReference.reference.replace("Medication/", "")
      : null) ||
    // Try to get from coding
    latestStatement?.medicationCodeableConcept?.coding?.[0]?.display ||
    latestAdmin?.medicationCodeableConcept?.coding?.[0]?.display ||
    "Unknown Medication";

  const dosage =
    latestAdmin?.dosage?.dose?.value && latestAdmin?.dosage?.dose?.unit
      ? `${latestAdmin.dosage.dose.value} ${latestAdmin.dosage.dose.unit}`
      : latestStatement?.dosage?.[0]?.text || "-";

  const route = latestAdmin?.dosage?.route?.text || "-";
  const status = latestAdmin?.status || latestStatement?.status || "-";

  return (
    <Box
      p={4}
      borderWidth="1px"
      borderColor="border.emphasized"
      borderRadius="md"
      bg="bg.subtle"
      mb={4}
    >
      <Box display="grid" gridTemplateColumns="auto 1fr" alignItems="center" gap={0}>
        <Text
          fontSize="sm"
          fontWeight="bold"
          whiteSpace="nowrap"
          py={2}
          px={3}
          borderRight="1px solid"
          borderBottom="1px solid"
          borderColor="border.emphasized"
          bg="bg.emphasized"
          color="fg.emphasized"
        >
          Name
        </Text>
        <Text
          fontSize="sm"
          py={2}
          px={3}
          borderBottom="1px solid"
          borderColor="border.emphasized"
          color="fg.emphasized"
        >
          {finalMedicationName}
        </Text>

        <Text
          fontSize="sm"
          fontWeight="bold"
          whiteSpace="nowrap"
          py={2}
          px={3}
          borderRight="1px solid"
          borderBottom="1px solid"
          borderColor="border.emphasized"
          bg="bg.emphasized"
          color="fg.emphasized"
        >
          Status
        </Text>
        <Box py={2} px={3} borderBottom="1px solid" borderColor="border.emphasized">
          <Badge size="sm" variant="subtle" colorScheme={getStatusCodeColor(status)}>
            {status}
          </Badge>
        </Box>

        <Text
          fontSize="sm"
          fontWeight="bold"
          whiteSpace="nowrap"
          py={2}
          px={3}
          borderRight="1px solid"
          borderBottom="1px solid"
          borderColor="border.emphasized"
          bg="bg.emphasized"
          color="fg.emphasized"
        >
          Dosage
        </Text>
        <Text
          fontSize="sm"
          py={2}
          px={3}
          borderBottom="1px solid"
          borderColor="border.emphasized"
          color="fg.emphasized"
        >
          {dosage}
        </Text>

        <Text
          fontSize="sm"
          fontWeight="bold"
          whiteSpace="nowrap"
          py={2}
          px={3}
          borderRight="1px solid"
          borderColor="border.emphasized"
          bg="bg.emphasized"
          color="fg.emphasized"
        >
          Route
        </Text>
        <Text fontSize="sm" py={2} px={3} color="fg.emphasized">
          {route}
        </Text>
      </Box>
    </Box>
  );
}

function MedicationTimelineItem({
  resource,
  isLast,
}: {
  resource: MedicationAdministration | MedicationDispense | MedicationStatement | MedicationRequest;
  isLast: boolean;
}) {
  const { title, date, details } = getMedicationResourceInfo(resource);

  return (
    <Box position="relative">
      {/* Timeline line */}
      {!isLast && (
        <Box
          position="absolute"
          left="12px"
          top="40px"
          bottom="-16px"
          width="2px"
          bg="status.info"
        />
      )}

      {/* Timeline dot */}
      <Box
        position="absolute"
        left="8px"
        top="8px"
        width="8px"
        height="8px"
        borderRadius="50%"
        bg="status.info"
        zIndex={1}
      />

      {/* Content */}
      <Box ml={8}>
        <HStack mb={2}>
          <Text fontSize="sm" fontWeight="semibold" color="fg.emphasized">
            {title}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {date}
          </Text>
        </HStack>

        <Box
          p={3}
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border.emphasized"
          borderRadius="md"
        >
          <VStack align="stretch" gap={2}>
            {details.map((detail, index) => (
              <HStack key={index} justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="fg.emphasized">
                  {detail.label}:
                </Text>
                <Box>
                  {typeof detail.value === "string" ? (
                    <Text fontSize="sm" color="fg.emphasized">
                      {detail.value}
                    </Text>
                  ) : (
                    detail.value
                  )}
                </Box>
              </HStack>
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}

function renderMedicationSeries(seriesData: Record<string, unknown>) {
  // Check if this is an EnhancedMedicationSeries with events
  const events = seriesData.events as
    | Array<{
        type: "administration" | "dispense" | "request" | "statement";
        date: string;
        resource:
          | MedicationAdministration
          | MedicationDispense
          | MedicationRequest
          | MedicationStatement;
      }>
    | undefined;

  if (events && events.length > 0) {
    // Sort events by date (newest first)
    const sortedEvents = [...events].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    // Get medication name from codeDisplay
    const medicationName = (seriesData.codeDisplay as string) || "Unknown Medication";

    return (
      <Box>
        <Heading size="md" mb={4} color="fg.emphasized">
          Medication Details
        </Heading>

        {/* Medication Details Section */}
        <MedicationDetailsSection
          resources={sortedEvents.map(e => e.resource)}
          medicationName={medicationName}
          bundle={undefined} // Bundle not available in this context
        />

        <Heading size="md" mb={4} mt={6} color="fg.emphasized">
          Medication Timeline ({sortedEvents.length})
        </Heading>
        <VStack align="stretch" gap={4}>
          {sortedEvents.map((event, index) => (
            <MedicationTimelineItem
              key={`${event.type}-${index}`}
              resource={event.resource}
              isLast={index === sortedEvents.length - 1}
            />
          ))}
        </VStack>
      </Box>
    );
  }

  return null;
}

function getMedicationResourceInfo(
  resource: MedicationAdministration | MedicationDispense | MedicationStatement | MedicationRequest
) {
  const resourceType = resource.resourceType;

  switch (resourceType) {
    case "MedicationAdministration": {
      const admin = resource as MedicationAdministration;
      const adminDate =
        admin.effectivePeriod?.start ?? admin.effectiveDateTime ?? admin.effectivePeriod?.end;
      const hasDosage = admin.dosage?.dose?.value && admin.dosage?.dose?.unit;

      return {
        title: "Medication Administered",
        date: dayjs(adminDate).format(`${ISO_DATE} hh:mm:ss A UTC`),
        details: [
          {
            label: "Dosage Route",
            value: admin.dosage?.route?.text || "-",
          },
          {
            label: "Dosage Rate",
            value: hasDosage ? `${admin.dosage?.dose?.value} ${admin.dosage?.dose?.unit}` : "-",
          },
          {
            label: "Status",
            value: (
              <Badge size="sm" variant="subtle" colorScheme={getStatusCodeColor(admin.status)}>
                {admin.status}
              </Badge>
            ),
          },
        ],
      };
    }

    case "MedicationDispense": {
      const dispense = resource as MedicationDispense;
      return {
        title: "Medication Dispensed",
        date: dayjs(dispense.whenHandedOver).format(ISO_DATE),
        details: [
          {
            label: "Dosage Quantity",
            value:
              dispense.quantity?.value && dispense.quantity?.unit
                ? `${dispense.quantity.value} ${dispense.quantity.unit}`
                : "-",
          },
          {
            label: "Status",
            value: (
              <Badge size="sm" variant="subtle" colorScheme={getStatusCodeColor(dispense.status)}>
                {dispense.status}
              </Badge>
            ),
          },
        ],
      };
    }

    case "MedicationStatement": {
      const statement = resource as MedicationStatement;
      const statementDate =
        statement.dateAsserted ??
        statement.effectivePeriod?.start ??
        statement.effectiveDateTime ??
        statement.effectivePeriod?.end;

      return {
        title: "Medication Documented",
        date: dayjs(statementDate).format(ISO_DATE),
        details: [
          {
            label: "Instructions",
            value: statement.dosage?.[0]?.text || "-",
          },
          {
            label: "Status",
            value: (
              <Badge size="sm" variant="subtle" colorScheme={getStatusCodeColor(statement.status)}>
                {statement.status}
              </Badge>
            ),
          },
        ],
      };
    }

    case "MedicationRequest": {
      const request = resource as MedicationRequest;
      return {
        title: "Medication Requested",
        date: dayjs(request.authoredOn).format(ISO_DATE),
        details: [
          {
            label: "Note",
            value: request.note?.[0]?.text || "-",
          },
          {
            label: "Status",
            value: (
              <Badge size="sm" variant="subtle" colorScheme={getStatusCodeColor(request.status)}>
                {request.status}
              </Badge>
            ),
          },
        ],
      };
    }

    default:
      return {
        title: "Unknown",
        date: "-",
        details: [],
      };
  }
}

function getRelatedMedicationResources(
  currentResource: unknown,
  administrations: MedicationAdministration[],
  dispenses: MedicationDispense[],
  statements: MedicationStatement[],
  requests: MedicationRequest[]
) {
  const currentResourceObj = currentResource as Record<string, unknown>;
  const currentResourceType = currentResourceObj.resourceType as string;

  // If current resource is a Medication, find all related resources by medication reference
  if (currentResourceType === "Medication") {
    const medicationId = currentResourceObj.id as string | undefined;
    if (!medicationId) return [];
    return [
      ...administrations.filter(
        admin =>
          admin.medicationReference?.reference?.includes(medicationId) ||
          admin.medicationCodeableConcept?.coding?.some(coding => coding.code === medicationId)
      ),
      ...dispenses.filter(
        dispense =>
          dispense.medicationReference?.reference?.includes(medicationId) ||
          dispense.medicationCodeableConcept?.coding?.some(coding => coding.code === medicationId)
      ),
      ...statements.filter(
        statement =>
          statement.medicationReference?.reference?.includes(medicationId) ||
          statement.medicationCodeableConcept?.coding?.some(coding => coding.code === medicationId)
      ),
      ...requests.filter(
        request =>
          request.medicationReference?.reference?.includes(medicationId) ||
          request.medicationCodeableConcept?.coding?.some(coding => coding.code === medicationId)
      ),
    ];
  }

  // If current resource is already a medication-related resource, find related ones
  const medicationId = getMedicationIdFromResource(currentResource);
  if (!medicationId) return [];

  return [
    ...administrations.filter(admin => getMedicationIdFromResource(admin) === medicationId),
    ...dispenses.filter(dispense => getMedicationIdFromResource(dispense) === medicationId),
    ...statements.filter(statement => getMedicationIdFromResource(statement) === medicationId),
    ...requests.filter(request => getMedicationIdFromResource(request) === medicationId),
  ];
}

function getMedicationIdFromResource(resource: unknown): string | null {
  const resourceObj = resource as Record<string, unknown>;

  // Check medicationReference
  const medicationRef = resourceObj.medicationReference as Record<string, unknown> | undefined;
  if (medicationRef?.reference) {
    return (medicationRef.reference as string).replace("Medication/", "");
  }

  // Check medicationCodeableConcept
  const medicationConcept = resourceObj.medicationCodeableConcept as
    | Record<string, unknown>
    | undefined;
  const coding = medicationConcept?.coding as Array<Record<string, unknown>> | undefined;
  if (coding && coding.length > 0 && coding[0]) {
    return coding[0].code as string;
  }

  return null;
}

function getDateFromMedicationRef(
  medicationRef:
    | MedicationAdministration
    | MedicationDispense
    | MedicationStatement
    | MedicationRequest
): string | undefined {
  let date;
  if (medicationRef.resourceType === "MedicationAdministration") {
    date =
      medicationRef.effectivePeriod?.start ??
      medicationRef.effectiveDateTime ??
      medicationRef.effectivePeriod?.end;
  } else if (medicationRef.resourceType === "MedicationDispense") {
    date = medicationRef.whenHandedOver;
  } else if (medicationRef.resourceType === "MedicationStatement") {
    date =
      medicationRef.dateAsserted ??
      medicationRef.effectivePeriod?.start ??
      medicationRef.effectiveDateTime ??
      medicationRef.effectivePeriod?.end;
  } else if (medicationRef.resourceType === "MedicationRequest") {
    date = medicationRef.authoredOn;
  }

  return date;
}

function getStatusCodeColor(status: string | undefined): string {
  switch (status) {
    case "on-hold":
    case "triaged":
      return "yellow";
    case "in-progress":
    case "active":
      return "teal";
    case "completed":
      return "green";
    case "cancelled":
    case "entered-in-error":
    case "stopped":
      return "red";
    default:
      return "gray";
  }
}
